/* eslint-disable no-nested-ternary */

import { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Icons from "components/CustomIcons";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSlider from "components/MDSlider";
import FormSelector from "components/FormSelector";
import { useTranslation } from "react-i18next";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { useMaterialUIController } from "context";

const validate = (values) => {
  const errors = {};
  ["deviceId"].forEach((field) => {
    if (!values[field]) errors[field] = "Required";
  });
  return errors;
};

const pctToByte = (v) => Math.round(v * 2.55);
const byteToPct = (v) => Math.round(v / 2.55);

function ActionAddEditForm(props) {
  const { onSubmit, actionType, devices, deviceId, selectedDevice, thermostatIPReducer } = props;
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [deviceToEdit, setDeviceToEdit] = useState({});
  const [onOffValue, setOnOffValue] = useState(
    actionType === "edit" && !Array.isArray(selectedDevice.value) ? selectedDevice.value : 0
  );
  const [analogValue, setAnalogValue] = useState(
    actionType === "edit" && !Array.isArray(selectedDevice.value)
      ? parseFloat(selectedDevice.value)
      : 0
  );

  const [zoneID, setZoneID] = useState(0);
  const [systemID, setSystemID] = useState(0);
  const [maxValueState, setMaxValueState] = useState("40");
  const [minValueState, setMinValueState] = useState("7");
  const [stepState, setStepState] = useState("1");

  const [rVal, setRVal] = useState(0);
  const [gVal, setGVal] = useState(0);
  const [bVal, setBVal] = useState(0);
  const [whiteVal, setWhiteVal] = useState(0);
  const [intensityVal, setIntensityVal] = useState(0);
  const [rgbTab, setRgbTab] = useState("color");

  useEffect(() => {
    if (actionType === "edit") {
      if (!Array.isArray(selectedDevice.value)) {
        if (selectedDevice.type === "device15") {
          const thermostatIp = thermostatIPReducer?.thermostats?.[selectedDevice.deviceNodeId];
          if (thermostatIp) {
            setOnOffValue(thermostatIp.onOff);
            setAnalogValue(parseFloat(thermostatIp.setpoint));
            setZoneID(parseInt(thermostatIp.zoneID, 10));
            setSystemID(parseInt(thermostatIp.systemID, 10));
            setMaxValueState(thermostatIp.maxValue);
            setMinValueState(thermostatIp.minValue);
            setStepState(thermostatIp.step);
          }
        } else {
          setOnOffValue(0);
          setAnalogValue(0);
        }
      } else {
        selectedDevice.value.forEach(({ featureType, featureValue }) => {
          if (featureType === "setpoint") setAnalogValue(featureValue);
          if (featureType === "onOff") setOnOffValue(featureValue);
          if (featureType === "color") {
            if (selectedDevice.type === "device3" && Array.isArray(featureValue)) {
              setRVal(featureValue[0] || 0);
              setGVal(featureValue[1] || 0);
              setBVal(featureValue[2] || 0);
            }
            if (selectedDevice.type === "device4" && typeof featureValue === "object") {
              setRVal(byteToPct(featureValue.r || 0));
              setGVal(byteToPct(featureValue.g || 0));
              setBVal(byteToPct(featureValue.b || 0));
            }
          }
          if (featureType === "white") setWhiteVal(featureValue);
          if (featureType === "intensity") setIntensityVal(featureValue);
        });
      }
    }
  }, [actionType, selectedDevice, thermostatIPReducer]);

  useEffect(() => {
    devices.forEach((d) => {
      if (d.nodeId === deviceId || d.nodeId === selectedDevice.deviceNodeId) setDeviceToEdit(d);
    });
  }, [deviceId, selectedDevice, devices]);

  const pctLabel = (v) => `${v}%`;
  const hasWhite = deviceToEdit.whiteValue;

  const getValueType = () => {
    switch (deviceToEdit.type) {
      case "device1":
        return onOffValue;
      case "device2":
        return analogValue;
      case "device5":
        return deviceToEdit.value ? analogValue : onOffValue;
      case "device8":
        return onOffValue;
      case "device9":
        return analogValue;
      case "device10":
        return [
          {
            featureType: "setpoint",
            featureValue: analogValue,
            featureSend: true,
            featureLabel: `${analogValue}ºC`,
          },
          {
            featureType: "onOff",
            featureValue: onOffValue,
            featureSend: true,
            featureLabel: t(
              deviceToEdit.onOffInverted
                ? onOffValue
                  ? "labelOff"
                  : "labelOn"
                : onOffValue
                ? "labelOn"
                : "labelOff"
            ).toUpperCase(),
          },
        ];
      case "device15":
        return [
          {
            featureType: "setpoint",
            featureValue: analogValue,
            featureSend: true,
            featureLabel: `${analogValue}ºC`,
            zoneID,
            systemID,
          },
          {
            featureType: "onOff",
            featureValue: onOffValue,
            featureSend: true,
            featureLabel: t(onOffValue ? "labelOn" : "labelOff").toUpperCase(),
            zoneID,
            systemID,
          },
        ];
      case "device3":
      case "device4":
        return [
          {
            featureType: "color",
            featureValue:
              deviceToEdit.type === "device3"
                ? [rVal, gVal, bVal]
                : { r: pctToByte(rVal), g: pctToByte(gVal), b: pctToByte(bVal) },
            featureSend: true,
            featureLabel: false,
          },
          {
            featureType: "intensity",
            featureValue: intensityVal,
            featureSend: true,
            featureLabel: pctLabel(intensityVal),
          },
          ...(hasWhite && whiteVal > 0
            ? [
                {
                  featureType: "white",
                  featureValue: whiteVal,
                  featureSend: true,
                  featureLabel: pctLabel(whiteVal),
                },
              ]
            : []),
          {
            featureType: "onOff",
            featureValue: onOffValue,
            featureSend: true,
            featureLabel: t(onOffValue ? "btnOn" : "btnOff").toUpperCase(),
          },
        ];
      default:
        return 0;
    }
  };

  const getValueText = () => {
    switch (deviceToEdit.type) {
      case "device1":
        return t(onOffValue ? "btnOn" : "btnOff").toUpperCase();
      case "device2":
        return pctLabel(analogValue);
      case "device5":
        return deviceToEdit.value
          ? pctLabel(analogValue)
          : t(onOffValue ? "btnDown" : "btnUp").toUpperCase();
      case "device8":
        return deviceToEdit.visualizationType === "label"
          ? (onOffValue === parseInt(deviceToEdit.valueForOn, 10)
              ? deviceToEdit.labelOn
              : deviceToEdit.labelOff
            ).toUpperCase()
          : t(
              onOffValue === parseInt(deviceToEdit.valueForOn, 10) ? "btnOn" : "btnOff"
            ).toUpperCase();
      case "device9":
        return deviceToEdit.visualizationType === "fixed"
          ? (
              deviceToEdit.fixedValues.find((f) => parseFloat(f.value) === Math.round(analogValue))
                ?.label || t("noMatch")
            ).toUpperCase()
          : `${analogValue}${deviceToEdit.sufix}`;
      case "device10":
        return `${analogValue}ºC, ${t(
          deviceToEdit.onOffInverted
            ? onOffValue
              ? "labelOff"
              : "labelOn"
            : onOffValue
            ? "labelOn"
            : "labelOff"
        ).toUpperCase()}`;
      case "device15":
        return `${analogValue}ºC, ${t(onOffValue ? "labelOn" : "labelOff").toUpperCase()}`;
      case "device3":
      case "device4":
        return `RGB ${rVal}/${gVal}/${bVal}% · ${t("intensity")} ${intensityVal}%${
          hasWhite ? ` · ${t("whiteL")} ${whiteVal}%` : ""
        } · ${t(onOffValue ? "btnOn" : "btnOff")}`.toUpperCase();
      default:
        return "";
    }
  };

  const submitData = () => {
    onSubmit({
      deviceNodeId: deviceToEdit.nodeId,
      name: deviceToEdit.name,
      type: deviceToEdit.type,
      value: getValueType(),
      valueText: getValueText(),
      delay: deviceToEdit.delay,
      roomName: deviceToEdit.roomName,
      key: selectedDevice.key,
    });
  };

  const pctSlider = (label, val, setter) => (
    <MDBox mt={2} mx={2}>
      <MDBox display="flex" justifyContent="space-between">
        <MDTypography variant="h6">{label}</MDTypography>
        <MDTypography variant="h6">{pctLabel(val)}</MDTypography>
      </MDBox>
      <MDSlider value={val} onChange={(v) => setter(v)} min={0} max={100} color="info" />
    </MDBox>
  );

  const rgbControls = () => (
    <TabContext value={rgbTab}>
      <Tabs
        value={rgbTab}
        onChange={(_, v) => setRgbTab(v)}
        textColor="inherit"
        indicatorColor="secondary"
        variant="fullWidth"
      >
        <Tab label={t("color").toUpperCase()} value="color" />
        <Tab label={t("intensity").toUpperCase()} value="int" />
        <Tab label={t("whiteL").toUpperCase()} value="white" disabled={!hasWhite} />
      </Tabs>
      <TabPanel value="color" sx={{ px: 1 }}>
        {pctSlider("R", rVal, setRVal)}
        {pctSlider("G", gVal, setGVal)}
        {pctSlider("B", bVal, setBVal)}
      </TabPanel>
      <TabPanel value="int" sx={{ px: 3, textAlign: "center" }}>
        <MDSlider
          value={intensityVal}
          onChange={(v) => setIntensityVal(v)}
          min={0}
          max={100}
          color="info"
        />
        <MDTypography variant="h4">{pctLabel(intensityVal)}</MDTypography>
      </TabPanel>
      <TabPanel value="white" sx={{ px: 3, textAlign: "center" }}>
        <MDSlider
          value={whiteVal}
          onChange={(v) => setWhiteVal(v)}
          min={0}
          max={100}
          color="info"
        />
        <MDTypography variant="h4">{pctLabel(whiteVal)}</MDTypography>
      </TabPanel>
    </TabContext>
  );

  const shutterIcon = () => {
    const v = deviceToEdit.value;
    if (v)
      return analogValue === 100
        ? "WindowShutter"
        : analogValue === 0
        ? "WindowShutterOpen"
        : "WindowShutterHalf";
    return onOffValue === 1 ? "WindowShutter" : "WindowShutterOpen";
  };

  const icon = () => {
    switch (deviceToEdit.type) {
      case "device1":
      case "device2":
      case "device3":
      case "device4":
        return (
          <Icons
            name={onOffValue ? "LightbulbOnOutline" : "LightbulbOutline"}
            size={40}
            color={darkMode ? "white" : "black"}
          />
        );
      case "device5":
        return <Icons name={shutterIcon()} size={40} color={darkMode ? "white" : "black"} />;
      case "device6":
        return deviceToEdit.visualizationType === "icon" ? (
          <Icons
            name={onOffValue ? deviceToEdit.iconOn : deviceToEdit.iconOff}
            size={40}
            color={darkMode ? "white" : "black"}
          />
        ) : null;
      case "device8":
        return deviceToEdit.visualizationType === "icon" ? (
          <Icons
            name={
              parseInt(deviceToEdit.valueForOn, 10) === onOffValue
                ? deviceToEdit.iconOn
                : deviceToEdit.iconOff
            }
            size={40}
            color={darkMode ? "white" : "black"}
          />
        ) : null;
      case "device10":
      case "device15":
        return <Icons name="Thermometer" size={40} color={darkMode ? "white" : "black"} />;
      default:
        return null;
    }
  };

  const controls = () => {
    switch (deviceToEdit.type) {
      case "device1":
      case "device10":
      case "device15":
      case "device3":
      case "device4":
        return (
          <Switch
            checked={deviceToEdit.onOffInverted ? !onOffValue : !!onOffValue}
            onChange={() => setOnOffValue(onOffValue ? 0 : 1)}
          />
        );
      case "device8": {
        const checked =
          onOffValue === parseInt(deviceToEdit.valueForOn, 10) ||
          onOffValue !== parseInt(deviceToEdit.valueForOff, 10);
        return (
          <Switch
            checked={checked}
            onChange={() =>
              setOnOffValue(
                checked
                  ? parseInt(deviceToEdit.valueForOff, 10)
                  : parseInt(deviceToEdit.valueForOn, 10)
              )
            }
          />
        );
      }
      default:
        return null;
    }
  };

  const valueControl = () => {
    switch (deviceToEdit.type) {
      case "device2":
      case "device5":
        if (deviceToEdit.type === "device5" && !deviceToEdit.value) return null;
        return (
          <MDBox mt={1} mx={2}>
            <MDSlider
              value={analogValue}
              onChange={(v) => setAnalogValue(v)}
              color="error"
              valueLabelDisplay="auto"
            />
          </MDBox>
        );
      case "device9": {
        const fixedLabel =
          deviceToEdit.fixedValues.find((f) => parseFloat(f.value) === Math.round(analogValue))
            ?.label || t("noMatch").toUpperCase();
        return (
          <MDBox minHeight={125}>
            {deviceToEdit.visualizationType === "fixed" && (
              <Grid container mt={1} mb={2} justifyContent="center">
                {deviceToEdit.fixedValues.map(({ value: v, label }) => (
                  <MDBox m={1} key={label}>
                    <MDButton color="info" onClick={() => setAnalogValue(v)}>
                      <MDTypography color="white" textTransform="capitalize">
                        {label}
                      </MDTypography>
                    </MDButton>
                  </MDBox>
                ))}
              </Grid>
            )}
            {deviceToEdit.visualizationType === "value" && (
              <MDBox mt={1} mx={2}>
                <MDSlider
                  value={analogValue}
                  onChange={(v) => setAnalogValue(v)}
                  min={parseFloat(deviceToEdit.minValue)}
                  max={parseFloat(deviceToEdit.maxValue)}
                  step={parseFloat(deviceToEdit.slowStep)}
                  color="error"
                  valueLabelDisplay="auto"
                />
              </MDBox>
            )}
            <MDBox mb={1} display="flex" justifyContent="center">
              <MDTypography variant="h2">
                {deviceToEdit.visualizationType === "value"
                  ? `${analogValue?.toFixed(deviceToEdit.decimalPlaces)} ${deviceToEdit.sufix}`
                  : fixedLabel.toUpperCase()}
              </MDTypography>
            </MDBox>
          </MDBox>
        );
      }
      case "device10":
        return (
          <MDBox minHeight={125}>
            <MDBox mt={1} mx={2}>
              <MDSlider
                value={analogValue}
                onChange={(v) => setAnalogValue(v)}
                min={parseFloat(deviceToEdit.minValue)}
                max={parseFloat(deviceToEdit.maxValue)}
                step={parseFloat(deviceToEdit.step)}
                color="error"
                valueLabelDisplay="auto"
              />
            </MDBox>
            <MDBox mb={1} display="flex" justifyContent="center">
              <MDTypography variant="h2">{`${analogValue.toFixed(1)}ºC`}</MDTypography>
            </MDBox>
          </MDBox>
        );
      case "device15":
        return (
          <MDBox minHeight={125}>
            <MDBox mt={1} mx={2}>
              <MDSlider
                value={analogValue}
                onChange={(v) => setAnalogValue(v)}
                min={parseFloat(minValueState)}
                max={parseFloat(maxValueState)}
                step={parseFloat(stepState)}
                color="error"
                valueLabelDisplay="auto"
              />
            </MDBox>
            <MDBox mb={1} display="flex" justifyContent="center">
              <MDTypography variant="h2">{`${analogValue.toFixed(1)}ºC`}</MDTypography>
            </MDBox>
          </MDBox>
        );
      case "device3":
      case "device4":
        return rgbControls();
      default:
        return null;
    }
  };

  const deviceModal = () => (
    <Card sx={{ height: "100%", overflow: "hidden" }}>
      <MDBox
        p={3}
        height="100%"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          {icon()}
          {deviceToEdit.type === "device3" || deviceToEdit.type === "device4" ? (
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                marginLeft: 8,
                backgroundColor:
                  whiteVal > 0
                    ? "rgb(0,0,0)"
                    : `rgb(${pctToByte(rVal)},${pctToByte(gVal)},${pctToByte(bVal)})`,
              }}
            />
          ) : null}
          {controls()}
        </MDBox>
        {valueControl()}
        <MDBox mt={1} textAlign="center">
          <MDTypography variant="body2" textTransform="capitalize">
            {deviceToEdit.name?.toUpperCase() || ""}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );

  const devicesIds = devices.map((d) => d.nodeId);
  const devicesNames = devices.map((d) => ({ uid: d.nodeId, name: d.name }));

  return (
    <MDBox py={3} width="100%">
      <form>
        <Grid container justifyContent="center" alignItems="center">
          <Card id="basic-info" sx={{ overflow: "visible", width: "100%" }}>
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("device")}
            </MDBox>
            <MDBox pt={3} pb={3} px={3}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Field
                    name="deviceId"
                    values={devicesIds}
                    names={devicesNames}
                    component={FormSelector}
                    label={t("device")}
                    disabled={actionType === "edit"}
                  />
                </Grid>
                {deviceId && <Grid size={{ xs: 12 }}>{deviceModal()}</Grid>}
                <Grid size={{ xs: 12 }}>
                  <MDBox display="flex" justifyContent="center" mt={12}>
                    <MDButton variant="gradient" color="dark" size="small" onClick={submitData}>
                      {t("Save")}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

ActionAddEditForm.defaultProps = {
  devices: [],
  deviceId: "",
  selectedDevice: {},
  thermostatIPReducer: {},
};

ActionAddEditForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  devices: PropTypes.array,
  deviceId: PropTypes.string,
  selectedDevice: PropTypes.object,
  thermostatIPReducer: PropTypes.object,
};

const selector = formValueSelector("ActionAddEditForm");

const mapStateToProps = (state, props) => ({
  deviceId: selector(state, "deviceId"),
  thermostatIPReducer: state.thermostatIPReducer || {},
  initialValues: { deviceId: props.selectedDevice.deviceNodeId },
});

export default connect(mapStateToProps)(
  reduxForm({ form: "ActionAddEditForm", validate })(ActionAddEditForm)
);
